
import {
    IIsiDtoCiaFunction, IIsiDtoCiaInstanceScreenForClient, IIsiDtoDestination,
    IIsiDtoPlCiaRequest, IIsiDtoPlCiaResult, IIsiDtoPlCiaSource, IIsiDtoSource, IIsiDtoSparkleForClient,
    IsiDtoCiaInstanceScreenForClient, IsiDtoPlCiaSource, IsiDtoSparkleForClient
} from "../../Dtos/Isilog.IsiComponent.IsiDataTransferObject.IsiPlCia";
import { IIsiUIFlowResultGeneric } from "../Core/IIsiUIFlowResultGeneric";
import { IsiPlCia } from "../PresentationLogic/IsiPlCia";

declare global {
    interface Clipboard {
        write?(items: ClipboardItem[]): Promise<void>;
    }
}
interface ClipboardItem {
}

declare var ClipboardItem: {
    prototype: ClipboardItem
    new(items: Record<string, Blob>): ClipboardItem
}

/**
 * Copie la réponse de l’IA dans le presse-papiers (HTML + texte brut).
 * - Si la case #inputCheckbox n’est pas cochée, supprime les <cite> (sources).
 * - Utilise l’API Clipboard (HTML + texte), fallback sur texte seul si non supporté.
 * - Affiche un message de confirmation via uiCore.
 */
export async function CopyIaResponse(idIaResponse:string) {

    const raw: string = document.getElementById(idIaResponse)?.innerHTML ?? ''
    const html: Document = new DOMParser().parseFromString(raw, 'text/html')

    const isCiteRemovalEnabled: boolean = !(document.getElementById('inputCheckbox') as HTMLInputElement)?.checked;

    if (isCiteRemovalEnabled) {

        Array.from(html.getElementsByTagName('cite')).forEach(elt => {

            html.querySelectorAll('cite').forEach(elt => {
                const previousSibling: ChildNode = elt.previousSibling

                if (previousSibling.nodeType === Node.TEXT_NODE) {
                    previousSibling.textContent = previousSibling.textContent.trimEnd()
                }
                elt.remove()
            })

        })

    }

    const bodyHtml: string = html.body.innerHTML
    const bodyText: string = html.body.textContent ?? ''

    try {
        if (navigator.clipboard && ClipboardItem) {

            const item:ClipboardItem = new ClipboardItem({
                'text/html': new Blob([bodyHtml], { type: 'text/html' }),
                'text/plain': new Blob([bodyText], { type: 'text/plain' }),
            })

            await navigator.clipboard.write?.([item])

        } else {

            await navigator.clipboard.writeText(bodyText)
        }

    } catch {
        await navigator.clipboard.writeText(bodyText)

    }

    const msg: string = uiCore.getMessage("CIA", "ciaMessageCopieReponseIa").IsiLabel
    uiCore.showMessage(msg)

}
window.CopyIaResponse = CopyIaResponse;

export class IsiCia {

    // Balises HTML utilisées pour styliser le contenu de la popin lors de l'affichage de la réponse IA :

    // Permet de sélectionner uniquement le contenu du span (Ctrl+A), sans curseur ni édition
    private readonly spanDivBehavior: string = `contenteditable="true" onbeforeinput="event.preventDefault()"`

    // Checkbox permettant d’inclure ou non les sources dans la copie de la réponse
    private readonly inputCheckboxVisible: string = `<label for="inputCheckbox" class="ciaInputCheckBoxVisibleLabel"><input type="checkbox" id="inputCheckbox" checked class="ciaInputCheckBoxVisible">${uiCore.getMessage("CIA", "ciaLabelCopierSources")}</label>`
    private readonly inputCheckboxInvisible: string = `<input type="checkbox" id="inputCheckbox" hidden>`

    _configCiaInstance: IsiDtoCiaInstanceScreenForClient
    constructor() {

        const hiddenField: HTMLInputElement | null = document.getElementById("ihdSparklesConfig") as HTMLInputElement | null

        try {
            if (hiddenField?.value) {
                this._configCiaInstance = JSON.parse(hiddenField.value)
            } else {
                const msg: string = uiCore.getMessage("CIA", "ciaErreurConfigurationIntrouvable").IsiLabel
                uiCore.log(msg)
            }
        }
        catch (e) {
            const msg: string = `${uiCore.getMessage("CIA", "ciaErreurConfigurationIntrouvable").IsiLabel}${hiddenField.value}}`
            uiCore.log(msg)
            uiCore.log(e.message)
        }

    }

    /**
     * Envoie une requête à l’IA via le PL CIA à partir d’un Sparkle donné.
     * Récupère les sources, construit la requête, envoie et affiche la réponse IA.
     * @param sparkleId ID du Sparkle cliqué.
     */
    public async CallIa(ctrlId: string, sparkleId: string) {

        const sparkleContext: {
            targetSparkleFunctions: IIsiDtoCiaFunction[],
            configObject: IIsiDtoCiaInstanceScreenForClient
        } | null = this._checkSparkleExecutionPreconditions(sparkleId)

        if (!sparkleContext) {
            return
        }

        const screenId: string = screenCore.currentScreen.name
        const pageName: string = screenCore.currentScreen.pageName
        const language: string = CURRENT_LANGUAGE
        const category: string = iwsAllCtrls.getCtrl("itxtL_TYPEPB")?.getText()??'';

        const rawClaim: string = iwsAllCtrls.getCtrl('ihdC_SIGNATURE').value()
        const claimNumber = isNaN(parseInt(rawClaim)) ? 0 : parseInt(rawClaim)

        let sparkleControlSet: {
            inputControls: { sparkleSource: IIsiDtoSource, sparkleFunction: IIsiDtoCiaFunction }[],
            outputControls: { sparkleDestination: IIsiDtoSource, sparkleFunction: IIsiDtoCiaFunction }[]
        }

        sparkleControlSet = this._extractControlsFromFunctions(sparkleContext.targetSparkleFunctions)

        const sources: IIsiDtoPlCiaSource[] = this._buildSources(sparkleControlSet.inputControls)
        const ctrl = iwsAllCtrls.getCtrl(ctrlId).getElement();
        const waitingSparkleClass = "waiting-request";

        ctrl.classList.add(waitingSparkleClass);

        const plCia = new IsiPlCia()

        try {

            const iaResponse: IIsiUIFlowResultGeneric<IIsiDtoPlCiaRequest> = await plCia.SendRequestToIAAsync(
                pageName,
                screenId,
                sparkleId,
                language,
                category,
                claimNumber,
                sources
            )

            this._buildDestinations(sparkleControlSet.outputControls, iaResponse)

        } catch (e) {

            const msg: string = uiCore.getMessage("CIA", "ciaErreurPasDeReponseIa").IsiLabel
            uiCore.showErrorMessage(msg)
            uiCore.log(e.message)

        } finally {

            ctrl.classList.remove(waitingSparkleClass);

        }

    }

    /**
     * Récupère les identifiants et types des sources et destinations 
     * à partir des fonctions d’un Sparkle.
     *
     * @param targetSparkleFunctions Fonctions du Sparkle à analyser.
     * @returns Un objet contenant :
     * - `inputControls` : les sources d’entrée ;
     * - `outputControls` : les destinations de sortie.
     */
    private _extractControlsFromFunctions(targetSparkleFunctions: IIsiDtoCiaFunction[]): {
        inputControls: { sparkleSource: IIsiDtoSource, sparkleFunction: IIsiDtoCiaFunction }[],
        outputControls: { sparkleDestination: IIsiDtoDestination, sparkleFunction: IIsiDtoCiaFunction }[]
    } {

        const inputControls: { sparkleSource: IIsiDtoSource, sparkleFunction: IIsiDtoCiaFunction }[] = []
        const outputControls: { sparkleDestination: IIsiDtoDestination, sparkleFunction: IIsiDtoCiaFunction }[] = []

        targetSparkleFunctions.forEach(sparkleFunction => {

            const sparkleSources: IIsiDtoSource[] = sparkleFunction.sources

            sparkleSources.forEach(sparkleSource => {
                inputControls.push({ sparkleSource, sparkleFunction })
            })

            const sparkleDestinations: IIsiDtoDestination[] = sparkleFunction.destinations
            sparkleDestinations.forEach(sparkleDestination => {
                outputControls.push({ sparkleDestination, sparkleFunction })
                
            })

        })

        return { inputControls: inputControls, outputControls: outputControls }

    }


    /**
     * Vérifie si les conditions sont réunies pour pouvoir appeler le PL.
     * Si toutes les conditions sont remplies, retourne la configuration et les fonctions associées au Sparkle.
     */
    private _checkSparkleExecutionPreconditions(sparkleId: string): {
        targetSparkleFunctions: IIsiDtoCiaFunction[],
        configObject: IIsiDtoCiaInstanceScreenForClient
    } | null {

        //Vérification si le champ caché contenant la configuration est présent
        if (!this._configCiaInstance) {
            const msg: string = uiCore.getMessage("CIA", "ciaErreurLicence").IsiLabel
            uiCore.showErrorMessage(msg)
            return null
        }

        const configObject: IIsiDtoCiaInstanceScreenForClient = this._configCiaInstance

        //Vérification si lors de l'appel un id de sparkle à bien été passé en paramètre
        if (!sparkleId) {
            const msg: string = uiCore.getMessage("CIA", "ciaErreurSparkle").IsiLabel
            uiCore.showErrorMessage(msg)
            return null
        }

        //Vérification si dans le fichier de config il existe bien une occurence pour l'id sparkle passé en paramètre
        let targetSparkleFunctions: IIsiDtoCiaFunction[] = []

        try {
            const targetSparkle: IIsiDtoSparkleForClient = configObject.sparkles.find(elt => elt.id === sparkleId)
            targetSparkleFunctions = targetSparkle.fonction
        } catch (e) {
            const msg: string = uiCore.getMessage("CIA", "ciaErreurSparkle").IsiLabel
            uiCore.showErrorMessage(msg)
            uiCore.log(e.message)
            return null
        }

        return { targetSparkleFunctions: targetSparkleFunctions, configObject: configObject }

    }

    /**
     * Construit la liste des sources à envoyer à l’IA à partir des contrôles configurés.
     * Récupère les valeurs si le type est "controle", sinon retourne une valeur vide.
     * @param inputControls Liste des identifiants et types de contrôle.
     * @returns Liste des objets source formatés pour la requête IA.
     */
    private _buildSources(inputControls: { sparkleSource: IIsiDtoSource, sparkleFunction: IIsiDtoCiaFunction }[]): IIsiDtoPlCiaSource[] {

        return inputControls.map(inputControl => {

            const type: string = inputControl.sparkleSource.type

            if (type === "controle") {

                let controlSource: IwsControle | null = iwsAllCtrls.getCtrl(inputControl.sparkleSource.idType)

                if (!controlSource) {

                    const msg: string = uiCore.getMessage("CIA", "ciaErreurFichierConfigMalRenseigne").IsiLabel
                    uiCore.showErrorMessage(msg)
                    return IsiDtoPlCiaSource.fromJS({
                        idSource: inputControl.sparkleSource.idType,
                        valueSource: ""
                    })

                }

                let ctrlSourceValue: string = controlSource.value().toString()

                return IsiDtoPlCiaSource.fromJS({
                    idSource: inputControl.sparkleSource.idType,
                    valueSource: ctrlSourceValue
                })
            }

            return IsiDtoPlCiaSource.fromJS({
                idSource: inputControl.sparkleSource.id,
                valueSource: ""
            })

        })

    }

    /**
     * Applique la réponse de l’IA aux contrôles de sortie.
     * Si le type est "controle", insère la réponse HTML dans le champ RICH.
     * Sinon, affiche la réponse HTML dans une POPIN.
     * @param outputControls Liste des contrôles de sortie à renseigner.
     * @param iaResponse Résultat retourné par l’appel à l’IA.
     */
    private _buildDestinations(outputControls: { sparkleDestination: IIsiDtoDestination, sparkleFunction: IIsiDtoCiaFunction }[], iaResponse: IIsiUIFlowResultGeneric<IIsiDtoPlCiaRequest>) {

        const sparkleMessageFamilly: string = "C_LOV_SPARKLES"

        let popinWidth: string = "min(calc(100% - 100px), 1600px)"
 
        let messages: [string, string][] = []

        let counter: number = 0

        outputControls.forEach(outputControl => {

            // Icône permettant de copier la réponse IA, en supprimant les <cite> si la case est décochée
            const spanCopy: string = `<span class="ciaSpanCopyIcon" onclick="CopyIaResponse('iaResponse${counter}')"><span class="fa fa-copy"></span></span>`;

            const responses: IIsiDtoPlCiaResult[] = iaResponse.reponse.response.getIaResponse ?? []
            const response: IIsiDtoPlCiaResult = responses.find(r => r.functionId === outputControl.sparkleFunction.id)

            let responseValue: string = ""
            const existingMessageIndex = messages.findIndex(msg => msg[0] === outputControl.sparkleDestination.id);

            if (response?.iaResponse != null) {

                const designedIaResponse: { responseWithoutSources: string, designedReferences: string } = this._designIaResponseAnswerReferences(response.iaResponse)

                if (existingMessageIndex === -1) {
                    
                    responseValue = `<span ${this.spanDivBehavior} class="ciaBorderedArea">${uiCore.getMessage("CIA", "ciaAttentionDonneesIA")}</span>`

                } 

                if (this._isThereSourcesInIaResponse(response.iaResponse)) {
                    responseValue += `<span class="ciaBorderedArea"><span class="ciaSpanCopy">${this.inputCheckboxVisible}${spanCopy}</span><br><br><span ${this.spanDivBehavior} id='iaResponse${counter}'>${designedIaResponse.responseWithoutSources}</span></span>`
                } else {
                    responseValue += `<span class="ciaBorderedArea"><span class="ciaSpanCopy">${this.inputCheckboxInvisible}${spanCopy}</span><br><br><span ${this.spanDivBehavior} id='iaResponse${counter}'>${designedIaResponse.responseWithoutSources}</span></span>`
                }

                responseValue += designedIaResponse.designedReferences
                counter++

            } else if (existingMessageIndex === -1) {

                responseValue = uiCore.getMessage("CIA", "ciaErreurPasDeReponseIa").IsiLabel
                
            }

            if (existingMessageIndex === -1) {
                messages.push([outputControl.sparkleDestination.id, responseValue])
            }else{
                messages[existingMessageIndex][1] += `<br><br>${responseValue}`
            }
                
        })

        messages.forEach(([idDestination, value]) => {
            
            const outputControl: { sparkleDestination: IIsiDtoDestination, sparkleFunction: IIsiDtoCiaFunction }
                = outputControls.find(outputControl => outputControl.sparkleDestination.id === idDestination)

            if (!outputControl) return

            const type: string = outputControl.sparkleDestination.type

            if (type === "controle") {
                iwsAllCtrls.getCtrl(outputControl.sparkleDestination.idType).setValue(value)
            } else {
                // On reprends le libellé associé à un sparkle, sinon on reprend le titre par défaut
                let popinTitle = getMessagesForJavascript().GetMessage(sparkleMessageFamilly + '#' + iaResponse.reponse.sparkleId);
                // Si le message n'existe pas, on utilise le titre par défaut.
                if (popinTitle.L_MESSAGE === popinTitle.C_MESSAGE) {
                    popinTitle = getMessagesForJavascript().GetMessage(sparkleMessageFamilly + '#SYNTHESE');
                }
                if (uiCore.isRWDPhone()) {
                    popinWidth = "calc(100% - 18px)";
                }

                uiCore.showMessage(value, { popin: true, title: popinTitle.L_MESSAGE, width: popinWidth, height: "calc(100% - 100px)", resizeToFitContent: false, mainClassName: "rich-font cia-content" })

            }
        })

    }

    /*-- CREATION -----------------------------------------------------------------------------
    '- Auteur      : YLAR
    '- Date        : 01/07/2025
    '- Description : Prend en compte la modif de visibilité des sparkles.
    '-----------------------------------------------------------------------------------------*/
    public EvalSparkleVisibility(jsonValue: string) {
        if (jsonValue) {
            try {
                const sparkles = JSON.parse(jsonValue);

                let spk: IsiDtoSparkleForClient;
                sparkles.sparkles.forEach(item => {
                    spk = this._configCiaInstance.sparkles.find(elt => elt.id === item.id);
                    if (spk)
                        iwsAllCtrls.getCtrl(spk.position).setSparkleVisible(item.Visibility);
                    else {
                        const msg: string = `${uiCore.getMessage("CIA", "ciaErreurSparkleNonDefiniDansConfig").IsiLabel}${item.id}`
                        uiCore.log(msg)
                    }
                })

            } catch (e) {
                const msg: string = uiCore.getMessage("CIA", "ciaErreurEvaluationVisibiliteSparkle").IsiLabel
                uiCore.log(msg)
                uiCore.log(e.message)
            }
        }
    }

    /**
     * Met en forme une réponse HTML générée par l'IA
     * puis extrait la balise <sources>, la transforme en éléments HTML stylisés, puis retourne la réponse nettoyée.
     *
     * @param response - Chaîne HTML contenant une réponse d'IA avec éventuellement une balise <sources>
     * @returns Un objet contenant :
     * - `responseWithoutSources` : la réponse HTML sans la balise <sources>
     * - `designedReferences` : une version HTML stylisée des sources extraites
     */
    private _designIaResponseAnswerReferences(response: string): { responseWithoutSources:string, designedReferences: string } {

        const responseClean: string = response
            .replace(/\n+/g, ' ')       // supprime les retours à la ligne
            .replace(/\s{2,}/g, ' ')    // remplace les espaces multiples par un espace
            .trim()                     // supprime les espaces parasites au tout début et à la fin de la réponse
            // Trouve toutes les paires de balises successives avec des espaces entre elles
            // Exemple capturé : "</p> <ul>" ou "<li>   <ul>"
            // On statue ensuite si on colle les balises ou si on insère un <br> entre elles
            .replace(/<\/?\w+[^>]*>\s+<\/?\w+[^>]*>/g, (match) => {
                if (
                    /<\/[uo]l>\s*<li/i.test(match) ||    // </ul> ou </ol> ... <li>
                    /<\/li>\s*<[uo]l/i.test(match) ||    // </li> ... <ul> ou <ol>
                    /<li>\s*<[uo]l/i.test(match) ||      // <li> ... <ul> ou <ol>
                    /<\/li>\s*<\/[uo]l/i.test(match) ||  // </li> ... </ul> ou <ol>
                    /<\/p>\s*<[uo]l/i.test(match)        // </p> ... <ul> ou <ol>
                ) {
                    // Supprime l’espace entre les deux balises
                    // Exemple : "</p> <ul>" → "</p><ul>"
                    return match.replace(/\s+/, ''); // supprime espace entre balises
                }
                // Sinon, on insère un saut de ligne visuel
                // Exemple : "</p> <p>" → "</p><br><p>"
                return match.replace(/\s+/, '<br>'); // sinon, ajoute <br>
            })
            .replace(/<\/[uo]l>\s*<p/gi, '</ul><br><p') // Ajoute des sauts de ligne entre les listes et les paragraphes
            .replace(/<[uo]l>\s*<li/gi, '<ul><br><li') // Ajoute des sauts de ligne entre l’ouverture d’une liste et son premier élément
            .replace(/>\s+</g, '><') // Supprime tous les espaces ou retours ligne entre balises HTML adjacentes.
            .replace(/(?:\s*<br\b[^>]*>\s*){2,}/gi, '<br>') // remplace les br successifs par un seul

        const parser: DOMParser = new DOMParser()
        const doc: Document = parser.parseFromString(responseClean, "text/html")

        const sourcesTag: HTMLElement | null = doc.querySelector("sources")

        const unformattedReferences: string = sourcesTag?.textContent?.trim() ?? ""
        if (!unformattedReferences) return { responseWithoutSources: doc.body.innerHTML, designedReferences: ""}

        const references: string[] =unformattedReferences.split(";")

        const formattedReferences: string = references.map(ref =>
            `<span>${this._escapeHtml(ref)}</span>`
        ).join(' ; ')

        sourcesTag?.remove();

        return {
            responseWithoutSources: doc.body.innerHTML,
            designedReferences: `<span ${this.spanDivBehavior} class="ciaBorderedArea">${uiCore.getMessage("CIA", "ciaPhraseIntroSources")}<span class="ciaSpanReferences">${formattedReferences}</span></span>`
        }

    }

    /**
    * Échappe les caractères spéciaux d'une chaîne HTML provenant de la balise <sources>
    * afin de prévenir toute tentative d'injection de code (XSS).
    *
    * @param unsafe - Une chaîne de texte non sécurisée issue de la balise <sources>
    * @returns Une chaîne "sanitisée", sûre à injecter dans le DOM
    */
    private _escapeHtml(unsafe: string): string {

        const safe: string = unsafe
            .replace(/&/g, "&amp;")   // Remplace les &
            .replace(/</g, "&lt;")    // Empêche l'ouverture de balises HTML ou <script>
            .replace(/>/g, "&gt;")    // Empêche la fermeture de balises HTML
            .replace(/"/g, "&quot;")  // Évite la rupture d'attribut HTML avec des double quotes
            .replace(/'/g, "&#039;")  // Pareil pour les single quotes

        return safe

    }

    /**
     * Vérifie si une réponse HTML de l'IA contient une balise <sources>.
     *
     * @param response Chaîne HTML représentant la réponse de l'IA.
     * @returns true si une balise <sources> est présente, false sinon.
     */
    private _isThereSourcesInIaResponse(response: string): boolean {

        const parser : DOMParser = new DOMParser()
        const doc: Document = parser.parseFromString(response, "text/html")
        const sourcesTag: HTMLElement | null = doc.querySelector("sources")
        return sourcesTag !== null

    }
}